import React from 'react';
import type { Inputs } from '../utils/roiCalculations';
import { DEFAULT_CLAWBACK_MONTHS, backendCommissionPerCustomer, backendOverridePerAccount, commissionsModeled, firstYearAcv, managerView, overrideBonusMonth, upfrontCommissionPerSale, upfrontOverridePerSale } from '../utils/commissions';
import { fmtMoney } from '../utils/formatters';
interface CommissionInputsProps {
  inputs: Inputs;
  onChange: (field: keyof Inputs, value: string) => void;
  /** Sets a non-numeric field (the perspective). */
  onSet: (field: keyof Inputs, value: string | number) => void;
  /** "sidebar" stacks label over field; "inline" puts the label to the left. */
  variant: 'sidebar' | 'inline';
}
type NumericKey = 'commissionPct' | 'clawbackMonths' | 'backendCommissionPct' | 'residualCommissionPct' | 'overrideUpfrontPct' | 'overrideBackendPct';
interface Field {
  key: NumericKey;
  label: string;
  unit: string;
  step: string;
  max: string;
  value: number | string;
}
const MONTH_OPTIONS = Array.from({
  length: 12
}, (_, i) => i + 1);

/**
 * Commission terms for residual products. In the company view: rep
 * commissions (upfront on first-year ACV, clawbacks, backend, residual). In
 * the manager view: the manager's override on each account, upfront plus a
 * backend reconciled against early cancels, paid rolling or as a yearly bonus.
 */
export function CommissionInputs({
  inputs,
  onChange,
  onSet,
  variant
}: CommissionInputsProps) {
  if (inputs.revenueCadence === 'one-time') return null;
  const manager = managerView(inputs);
  const clawbackField: Field = {
    key: 'clawbackMonths',
    label: 'Clawback Window',
    unit: 'mo',
    step: '1',
    max: '24',
    value: inputs.clawbackMonths ?? DEFAULT_CLAWBACK_MONTHS
  };
  const fields: Field[] = manager ? [{
    key: 'overrideUpfrontPct',
    label: 'Upfront Override (% of 1st-yr ACV)',
    unit: '%',
    step: '0.5',
    max: '100',
    value: inputs.overrideUpfrontPct ?? 0
  }, clawbackField, {
    key: 'overrideBackendPct',
    label: 'Backend Override (% of 1st-yr ACV)',
    unit: '%',
    step: '0.5',
    max: '100',
    value: inputs.overrideBackendPct ?? 0
  }] : [{
    key: 'commissionPct',
    label: 'Upfront Commission (% of 1st-yr ACV)',
    unit: '%',
    step: '1',
    max: '100',
    value: inputs.commissionPct ?? 0
  }, clawbackField, {
    key: 'backendCommissionPct',
    label: 'Backend Commission (% of 1st-yr ACV)',
    unit: '%',
    step: '1',
    max: '100',
    value: inputs.backendCommissionPct ?? 0
  }, {
    key: 'residualCommissionPct',
    label: 'Residual Commission (% of each payment)',
    unit: '%',
    step: '1',
    max: '100',
    value: inputs.residualCommissionPct ?? 0
  }];
  const bonusMonth = overrideBonusMonth(inputs);
  const acv = fmtMoney(firstYearAcv(inputs));
  const summary = manager ? `First-year ACV ${acv} · ${fmtMoney(upfrontOverridePerSale(inputs))} upfront per account; ${fmtMoney(backendOverridePerAccount(inputs))} backend per account in good standing, minus the upfront override on accounts that cancel within the window. ${bonusMonth ? `Backend paid as one bonus in Month ${bonusMonth} on accounts past their window, then rolling as later windows close.` : 'Backend paid as each window closes.'} Gross profit becomes override income; margin and rep commissions are ignored.` : commissionsModeled(inputs) ? `First-year ACV ${acv} · ${fmtMoney(upfrontCommissionPerSale(inputs))} upfront per sale, clawed back on cancels within the window${backendCommissionPerCustomer(inputs) > 0 ? `; ${fmtMoney(backendCommissionPerCustomer(inputs))} backend per customer still active when it closes` : ''}. Margin is now before commissions.` : 'Leave all at 0% if commissions are already inside your margin.';
  const sidebar = variant === 'sidebar';
  const perspectiveToggle = <div role="group" aria-label="Model profitability for" className="grid grid-cols-2 gap-1 rounded border border-gray-200 bg-white p-0.5" data-id="element-422" data-mp="28ajwt6ikzfhg">
      {([['company', 'Company'], ['manager', 'Manager']] as const).map(([key, label]) => {
      const active = (inputs.modelPerspective ?? 'company') === key;
      return <button key={key} type="button" aria-pressed={active} onClick={() => onSet('modelPerspective', key)} className={`rounded px-2 py-1 text-xs font-semibold whitespace-nowrap transition-colors ${active ? 'bg-navy text-white' : 'text-gray-600 hover:text-navy'}`} data-id="element-423" data-mp="2r7cowzg0iaa7">
            {label}
          </button>;
    })}
    </div>;
  const bonusSelect = manager ? <select value={bonusMonth} onChange={e => onSet('overrideBonusMonth', Number(e.target.value))} className={sidebar ? 'w-full p-2 rounded border border-gray-300 bg-white text-sm focus:border-amber outline-none' : 'w-full p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm'} data-id="element-424" data-mp="3tk5r96fmoj6j">
      <option value={0} data-id="element-425" data-mp="39vdcv3hej2n7">Rolling (as each window closes)</option>
      {MONTH_OPTIONS.map(m => <option key={m} value={m} data-id="element-426" data-mp="3en599un8o2l9">
          Bonus in Month {m}
        </option>)}
    </select> : null;
  if (sidebar) {
    return <div className="space-y-3 rounded border border-gray-200 bg-gray-50 p-2.5" data-id="element-427" data-mp="30x1rpgzb0x25">
        <div className="text-xs font-bold text-navy uppercase tracking-wide" data-id="element-428" data-mp="35v39zzowosck">
          {manager ? 'Manager Overrides' : 'Rep Commissions'}
        </div>
        <div data-id="element-429" data-mp="2vvuj5fpc7tuf">
          <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-430" data-mp="2vwnfrnul12yu">
            Model profitability for
          </label>
          {perspectiveToggle}
        </div>
        {fields.map(field => <div key={field.key} data-id="element-431" data-mp="280iokteoh1f4">
            <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-432" data-mp="27zhf2tdb8mzi">
              {field.label}
            </label>
            <div className="relative" data-id="element-433" data-mp="22zd0j8vyq2bx">
              <input type="number" min="0" max={field.max} step={field.step} value={field.value} onChange={e => onChange(field.key, e.target.value)} className="w-full p-2 pr-8 rounded border border-gray-300 bg-white text-sm focus:border-amber outline-none" data-id="element-434" data-mp="1y3ntyllizl64" />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs" data-id="element-435" data-mp="2gtm3z78vvoce">
                {field.unit}
              </span>
            </div>
          </div>)}
        {bonusSelect && <div data-id="element-436" data-mp="15cvzbsk3z4kw">
            <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-437" data-mp="15cdy0hila064">
              Backend Payout
            </label>
            {bonusSelect}
          </div>}
        <p className="text-[11px] leading-snug text-gray-600" data-id="element-438" data-mp="3m1f6j6dyinzu">{summary}</p>
      </div>;
  }
  return <>
      <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-439" data-mp="3qyouehlp6rlk">
        <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-440" data-mp="3vqozeogmkap0">
          Model Profitability For
        </label>
        <div className="w-56" data-id="element-441" data-mp="4j42m9s8idu7">{perspectiveToggle}</div>
      </div>
      {fields.map(field => <div key={field.key} className="flex items-center gap-3 w-full max-w-xl" data-id="element-442" data-mp="8k612oebulv4">
          <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-443" data-mp="dllp8pbsx8hq">
            {field.label}
          </label>
          <div className="w-56 relative" data-id="element-444" data-mp="ij549rgbppaq">
            <input type="number" min="0" max={field.max} step={field.step} value={field.value} onChange={e => onChange(field.key, e.target.value)} className="w-full p-2 pr-9 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-445" data-mp="2fc6vfuovl2ja" />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-446" data-mp="zsprbyybfbjv">
              {field.unit}
            </span>
          </div>
        </div>)}
      {bonusSelect && <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-447" data-mp="73ikmcopjfnj">
          <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-448" data-mp="258nvxnc46zt">
            Backend Payout
          </label>
          <div className="w-56" data-id="element-449" data-mp="h2rlzqlwjwjx">{bonusSelect}</div>
        </div>}
      <p className="w-full max-w-xl pl-[11.75rem] text-xs leading-snug text-gray-600" data-id="element-450" data-mp="c3en27hraz0z">
        {summary}
      </p>
    </>;
}